import Image from 'next/image';
import React from 'react';
import Link from 'next/link';
import { HeaderContainer } from 'structure/Layouts/SFILayouts/BaseLayout/components/HeaderContainer';
import { appConfig } from 'utils/app.config';
import { Body2, Normal2, Tiny1, Title4 } from 'components/Typography';
import { ConnectInfoButton } from 'components/Header/ConnectInfoButton';
import {
  MENU,
  TESTNET_LINKS,
  TESTNET_NAV_LINKS,
} from 'structure/Layouts/SFILayouts/BaseLayout/components/HeaderCommon/menu';
import { Button } from 'structure/home/components/Button';
import { headerStatus } from 'interfaces/header-status.enum';
import { useTranslation } from 'react-i18next';
import { getStaticAssetUrl } from 'utils/url';

export const HeaderCommonDesktop = ({
  isBannerClosed,
}: {
  isBannerClosed?: boolean;
}) => {
  const { t } = useTranslation('common');
  return (
    <div
      className={`fixed flex h-24 items-center border-b border-customBorder max-lg:border-b-0 ${!appConfig.featureFlags.ENABLE_BANNER || isBannerClosed ? 'top-0' : 'top-8'} z-20 w-full bg-box-primary`}
    >
      <HeaderContainer className='relative mx-auto flex flex-row items-center'>
        <Link href='/'>
          <Image
            className='h-[70px]'
            alt='Singularity Finance'
            src={getStaticAssetUrl(
              '/sfi/static/logo/singularity_finance_full_logo.svg'
            )}
            width={209}
            height={50}
          />
        </Link>
        <div className='ml-auto flex flex-row items-center gap-5 lg:gap-[2.5rem] xl:gap-[2.5rem]'>
          <DesktopMenu />
          <div className='group/item flex cursor-pointer items-center gap-1'>
            <Title4 className='flex items-center justify-center gap-2 font-normal text-tab-color'>
              Testnet{' '}
              <Tiny1 className='text-negative-800'>
                {t('LINK_STATUS.' + headerStatus.ENDED)}
              </Tiny1>{' '}
              <Image
                alt='arrow'
                src={getStaticAssetUrl('/sfi/static/svgs/arrow-down.svg')}
                height={10}
                width={10}
              />
            </Title4>
            <div
              className={`pointer-events-none absolute left-1/2 top-[3rem] mx-auto w-full -translate-x-1/2 -translate-y-5 transform pt-6 opacity-0 transition-all group-hover/item:pointer-events-auto group-hover/item:translate-y-0 group-hover/item:opacity-100 xl:w-fit`}
            >
              <div className='mx-4 border-[0.5px] border-white xl:mx-auto'>
                <div className='grid w-full grid-cols-3 xl:w-[960px]'>
                  <div className='col-span-2 bg-light-textPrimary py-6'>
                    <div className='flex'>
                      <div className='flex w-1/2 flex-col gap-y-4'>
                        {TESTNET_NAV_LINKS?.slice(0, 4).map((item, index) => (
                          <Link
                            href={item.href}
                            key={index}
                            target={item.target}
                            className={`group/edit flex flex-col px-6 py-1 ${item.order} ${item.status === headerStatus.ENDED && 'pointer-events-none cursor-not-allowed opacity-50'} hover:bg-white`}
                          >
                            <Normal2 className='flex items-center font-bold text-white group-hover/edit:text-light-textPrimary'>
                              {item.title}
                            </Normal2>
                            <Body2 className='font-light text-mono-100 group-hover/edit:text-light-textPrimary'>
                              {item.subTitle}
                            </Body2>
                          </Link>
                        ))}
                      </div>
                      <div className='flex w-1/2 flex-col gap-y-4'>
                        {TESTNET_NAV_LINKS?.slice(4).map((item, index) => (
                          <Link
                            href={item.href}
                            key={index}
                            target={item.target}
                            className={`group/edit flex flex-col px-6 py-1 ${item.order} ${item.status === headerStatus.ENDED && 'pointer-events-none cursor-not-allowed opacity-50'} hover:bg-white`}
                          >
                            <Normal2 className='flex items-center font-bold text-white group-hover/edit:text-light-textPrimary'>
                              {item.title}
                            </Normal2>
                            <Body2 className='font-light text-mono-100 group-hover/edit:text-light-textPrimary'>
                              {item.subTitle}
                            </Body2>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col bg-dropDown-backgroundLight py-4 text-white-200'>
                    {TESTNET_LINKS?.map((item, index) => (
                      <Link
                        key={index}
                        href={item.href}
                        target={item.target}
                        className={`group px-6 py-2 hover:bg-white ${item.disabled && 'pointer-events-none cursor-not-allowed opacity-50'}`}
                      >
                        <Normal2 className='flex gap-2 font-bold text-white-200 group-hover:text-light-textPrimary'>
                          {item.link}
                          <Image
                            src={getStaticAssetUrl(
                              '/sfi/static/svgs/right_arrow.svg'
                            )}
                            width={14}
                            height={10}
                            alt=''
                          />
                        </Normal2>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ConnectInfoButton />
        </div>
      </HeaderContainer>
    </div>
  );
};

const DesktopMenu = () => {
  const { t } = useTranslation('common');
  return (
    <div className='ml-auto flex flex-row items-center gap-5 lg:gap-[2.5rem] xl:gap-[2.5rem]'>
      {MENU?.filter((item) => item.visible)?.map((item, index) => (
        <Button
          key={index}
          className={`p-0 text-base ${item.status === headerStatus.COMING_SOON && 'pointer-events-none'}`}
          style='link'
          size='unspecified'
          component={Link}
          href={item.href}
          target={item?.target}
        >
          {item.title}
          {item.status && (
            <Tiny1 className='ps-1 text-[#EDFF6D]'>
              {' '}
              {t('LINK_STATUS.' + item.status)}
            </Tiny1>
          )}
        </Button>
      ))}
    </div>
  );
};
